/**
 * 튜토리얼 진행 상태머신 — 순수 로직(Phaser/외부 의존 0, 100% 단위테스트 대상).
 *
 * 한 시나리오의 스텝 목록 + 현재 인덱스만 관리한다. 화면 렌더/입력/저장은 `TutorialRunner`가
 * 이 컨트롤러를 구동하며 담당한다(관심사 분리). 재개(startIndex)·다시보기(reset)·액션 게이팅을 지원한다.
 */
import type { TutorialScenario, TutorialStep } from "./types.js";

export class TutorialController {
  private index: number;

  constructor(
    private readonly steps: TutorialScenario,
    startIndex = 0,
  ) {
    this.index = clampStart(startIndex, steps.length);
  }

  /** 현재 스텝 인덱스(완료 시 steps.length). */
  get currentIndex(): number {
    return this.index;
  }

  /** 전체 스텝 수. */
  get length(): number {
    return this.steps.length;
  }

  /** 현재 스텝(완료/빈 시나리오면 null). */
  get currentStep(): TutorialStep | null {
    return this.steps[this.index] ?? null;
  }

  /** 진행 중인가(스텝이 있고 아직 완료 전). */
  get isActive(): boolean {
    return this.steps.length > 0 && this.index < this.steps.length;
  }

  /** 모든 스텝을 소진했는가(빈 시나리오 포함). */
  get isComplete(): boolean {
    return this.index >= this.steps.length;
  }

  /**
   * 다음 스텝으로 진행. 새 현재 스텝을 반환(완료되면 null).
   * 이미 완료 상태면 그대로 null.
   */
  advance(): TutorialStep | null {
    if (this.index < this.steps.length) this.index += 1;
    return this.currentStep;
  }

  /**
   * 액션 발생 통지. 현재 스텝이 advance="action"이고 actionKey가 일치하면 진행한다.
   * @returns 이 통지로 진행했으면 true.
   */
  notifyAction(actionKey: string): boolean {
    const step = this.currentStep;
    if (step && step.advance === "action" && step.actionKey === actionKey) {
      this.advance();
      return true;
    }
    return false;
  }

  /** 처음(또는 지정 인덱스)으로 되감기 — 다시보기용. */
  reset(startIndex = 0): void {
    this.index = clampStart(startIndex, this.steps.length);
  }
}

/** startIndex를 [0, length] 범위로 클램프(length면 완료 상태로 시작 = 재개 데이터 손상 방어). */
function clampStart(startIndex: number, length: number): number {
  if (!Number.isFinite(startIndex) || startIndex < 0) return 0;
  return Math.min(Math.floor(startIndex), length);
}
